<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">

    <div class="row no-gutter h-100">
      <div class="col-3" v-if="showList">
        <div v-if="showDateFilter">
          <ReportFilterPopover
            headerTitle="Session Filter"
            :showApplyButton="true"
            :showResetButton="true"
            :appendToBody="true"
            alignment="left"
            placement="bottom-end"
            @apply="applySessionFilter"
            @reset="resetSessionFilter">
            <template v-slot:popover>
              <ReportDateRangeSelect 
                :startDate="editingSessionFilter.startDate"
                :endDate="editingSessionFilter.endDate"
                @updateStartDate="editingSessionFilter.startDate = $event"
                @updateEndDate="editingSessionFilter.endDate = $event"
              />
            </template>
            <template v-slot:filterDisplay>
              <div>
                <i class="far fa-calendar mr-2"></i>{{appliedSessionFilter.startDate | formatDate}} - {{appliedSessionFilter.endDate | formatDate}}
              </div>                
            </template>
          </ReportFilterPopover>
        </div>
        <StaticDataTable class="static-table-list" v-if="hasChartData" :smallRows="true">
          <template v-slot:header>
            <tr>
              <th width="60%">{{sessionInfoTableData.nameTitle}}</th>
              <th width="40%">{{sessionInfoTableData.valueTitle}}</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="(dataValue, index) of sessionInfoTableData.data" :key="index">
              <td>{{dataValue.name}}</td>
              <td><span v-html="dataValue.displayValue" /></td>
            </tr>
          </template>
        </StaticDataTable>
      </div>
      <div :class="showList ? 'col-9' : 'col-12'">
        <ChartDoughnut
          :series="sessionInfoSeries"
          :legend="sessionInfoLegend"
          :options="sessionInfoOptions"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import Utils from '@/utils/utils'
import AnalyticsDateMixin from '@/modules/intelligence/mixins/analyticsDate'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import ChartDoughnut from '@/components/charts/chartDoughnut'

import { ProductionPanelSessionTypes, OrderHoldTypeLookup } from '@/store/types'

export default {
  name: 'ProductionSessionChartWidget',
  mixins: [AnalyticsDateMixin],
  components: {
    AnalyticsWidget,
    ChartDoughnut,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean,
    showList: Boolean,
    showDateFilter: Boolean,
    showLegend: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      sessionInfoSeries: [],
      sessionInfoTableData: {},
      sessionInfoLegend: [],
      sessionInfoOptions: {},

      defaultSessionFilter: {    
        startDate: moment().local().subtract(90, 'days').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
        productionStageIdList: []
      },
      editingSessionFilter: {},
      appliedSessionFilter: {}
    }
  },
  computed: {
    hasChartData() {
      if(this.sessionInfoSeries) {
        return this.sessionInfoSeries.some(series => {
          if(series.data && series.data.length > 0)
            return true
        })
      }
      return false
    },
  },
  mounted() {
    this.editingSessionFilter = _.clone(this.defaultSessionFilter)
    this.appliedSessionFilter = _.clone(this.defaultSessionFilter)

    this.requestChartData()
  },
  beforeDestroy() {
  },
  watch: {
    filter: function() {
      this.requestChartData()
    }
  },
  methods: {
    applySessionFilter() {
      this.appliedSessionFilter = _.clone(this.editingSessionFilter)

      this.requestChartData()
    },
    resetSessionFilter() {
      this.editingSessionFilter = _.clone(this.defaultSessionFilter)
      this.appliedSessionFilter = _.clone(this.defaultSessionFilter)

      this.requestChartData()
    },
    requestChartData() {
      var summaryFilter = {
        startDate: this.appliedSessionFilter.startDate,
        endDate: this.appliedSessionFilter.endDate,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null,
        maxGroupSize: 10
      }

      this.$apiService.analyticsService.getProductionStageSessionSummary(summaryFilter)
      .then(response => {
        this.sessionInfoOptions.tooltip = {
          formatter(param) {
            return `${param.seriesName} <br />${param.name} : ${param.value} mins (${param.percent}%)`
          }
        }

        let groups = response.data.map(sessionInfo => {
          let name = ''

          if(sessionInfo.type == ProductionPanelSessionTypes.idle)
            name = 'Idle at ' + sessionInfo.productionStageName
          else if(sessionInfo.type == ProductionPanelSessionTypes.despatch)
            name = 'In Despatch at ' + sessionInfo.despatchTaskTypeName
          else if(sessionInfo.type == ProductionPanelSessionTypes.production)
            name = 'In Production at ' + sessionInfo.productionStationName
          else if(sessionInfo.type == ProductionPanelSessionTypes.hold)
            name = 'On Hold for ' + this.formatLookupName(sessionInfo.holdType, OrderHoldTypeLookup)

          return {
            name: name,
            value: Utils.roundNumber(sessionInfo.averageSessionTime / 60, 2) // Convert to minutes
          }
        })

        this.sessionInfoSeries = [{
          name: 'Average Session Time',
          data: groups,
          label: {
            show: true,
            formatter(param) {
              return param.name
            }
          },
        }]

        this.sessionInfoLegend = {
          data: groups.map(group => group.name)
        }
        
        this.sessionInfoTableData = {
          nameTitle: 'Session Type',
          valueTitle: 'Average Time',
          data: groups.map(group => {
            return {
              name: group.name,
              displayValue: Utils.formatDecimal(group.value, 2, true) + ' mins'
            }
          })
        }

        this.sessionInfoLegend.show = this.showLegend
      })
    },
  }
}
</script>